import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { OnboardingHandoffService } from '../services/onboarding-handoff.service';
import { APP_ROUTES } from '../constants/app-routes.const';

/**
 * A workspace with no playbook and no ICP cannot answer anything, so until the import has produced
 * them the knowledge base is the only screen worth being on. 'NONE' is not a gate: organisations
 * that predate the website import, and members invited into a ready one, never ran it.
 */
export const isOnboardingIncomplete = (status: string | undefined | null): boolean => status === 'PENDING' || status === 'FAILED';

/**
 * Nobody can be held on a screen they are not allowed to open. A member whose team has the
 * knowledge base withheld would otherwise be sent to it by this gate and bounced straight back by
 * the capability guard — one loop per navigation, with no way out of the app.
 */
export const isHeldByOnboarding = (auth: AuthService): boolean =>
  auth.hasCapability('knowledgeBase') && isOnboardingIncomplete(auth.currentUser()?.organisation?.onboardingStatus);

/** What stays reachable while the workspace is still being built: the build itself, and billing. */
const OPEN_WHILE_ONBOARDING = [
  'knowledge-base',
  'create-new-document',
  'user-settings',
  'manage-organisations',
  'subscription-required',
  'faq',
  'feedback',
];

/**
 * The same answer the gate gives, for anything that has to look like the gate: a rail that locks an
 * item the guard would have let through is a dead click, and one that leaves a gated item open is a
 * link that bounces back.
 */
export const isOpenWhileOnboarding = (url: string): boolean => {
  const path = url.split('?')[0].replace(/^\/main\/?/, '');
  return OPEN_WHILE_ONBOARDING.some(open => path.startsWith(open));
};

export const onboardingGateGuard: CanActivateChildFn = async (_route, state: RouterStateSnapshot) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const handoff = inject(OnboardingHandoffService);

  if (!auth.currentUser()) {
    await auth.resolveUserFromSession();
  }

  // Once handed over, the gate is done for this session — a stale status must not close it again.
  if (handoff.handedOver()) return true;
  if (!isHeldByOnboarding(auth)) return true;

  if (isOpenWhileOnboarding(state.url)) return true;

  return router.createUrlTree([APP_ROUTES.KNOWLEDGE_BASE]);
};

/**
 * The knowledge base is a screen of its own only while it is being built. Once the workspace is
 * ready it is a settings section, and every link to the old address lands there.
 */
export const knowledgeBaseEntryGuard: CanActivateFn = async () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const handoff = inject(OnboardingHandoffService);

  if (!auth.currentUser()) {
    await auth.resolveUserFromSession();
  }

  if (!handoff.handedOver() && isHeldByOnboarding(auth)) return true;

  return router.createUrlTree([`${APP_ROUTES.USER_SETTINGS}/knowledge-base`]);
};
